
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Download, X, FileImage, FileText } from "lucide-react";

interface TeacherFormatDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (format: 'jpg' | 'pdf') => void;
  downloadType: 'profile' | 'list';
  teacherName?: string;
}

const TeacherFormatDialog = ({ 
  isOpen, 
  onClose, 
  onDownload,
  downloadType,
  teacherName
}: TeacherFormatDialogProps) => {
  const [selectedFormat, setSelectedFormat] = useState<'jpg' | 'pdf'>('jpg');

  const handleDownload = () => {
    onDownload(selectedFormat);
    onClose();
  };

  const getTitle = () => {
    switch (downloadType) {
      case 'profile': return 'Teacher Profile';
      case 'list': return 'Teachers List';
      default: return 'Teacher Document';
    }
  };

  const getDescription = () => {
    switch (downloadType) {
      case 'profile': return `Download teacher profile${teacherName ? ` for ${teacherName}` : ''}`;
      case 'list': return 'Download teachers list with all information';
      default: return 'Download teacher document';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Download {getTitle()}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
          <DialogDescription>
            {getDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <RadioGroup value={selectedFormat} onValueChange={(value) => setSelectedFormat(value as 'jpg' | 'pdf')}>
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
              <RadioGroupItem value="jpg" id="jpg" />
              <div className="flex items-center space-x-3 flex-1">
                <FileImage className="h-5 w-5 text-blue-600" />
                <div>
                  <Label htmlFor="jpg" className="text-base font-medium cursor-pointer">
                    JPG Image
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    High-quality image format, ultra-fast download
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
              <RadioGroupItem value="pdf" id="pdf" />
              <div className="flex items-center space-x-3 flex-1">
                <FileText className="h-5 w-5 text-red-600" />
                <div>
                  <Label htmlFor="pdf" className="text-base font-medium cursor-pointer">
                    PDF Document
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Professional document format, perfect for printing
                  </p>
                </div>
              </div>
            </div>
          </RadioGroup>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download {selectedFormat.toUpperCase()}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeacherFormatDialog;
